import { DispositionCodes } from './disposition-code.model'

export enum SkillLevel {
  Beginner = 'Beginner',
  Intermediate = 'Intermediate',
  Expert = 'Expert',
}

export const skillLevelRank = {
  [SkillLevel.Beginner]: 0,
  [SkillLevel.Intermediate]: 1,
  [SkillLevel.Expert]: 2,
}

export enum DegreeLevel {
  NotSpecified = 'NotSpecified',
  HighSchool = 'HighSchool',
  VocationalHsDiploma = 'VocationalHsDiploma',
  VocationalDegree = 'VocationalDegree',
  Associates = 'Associates',
  Bachelors = 'Bachelors',
  Masters = 'Masters',
  PhD = 'PhD',
}

export const degreeLevelMappings = new Map<DegreeLevel, string>()
  .set(DegreeLevel.NotSpecified, 'Not Specified')
  .set(DegreeLevel.HighSchool, 'High School')
  .set(DegreeLevel.VocationalHsDiploma, 'Vocational High School Diploma')
  .set(DegreeLevel.VocationalDegree, 'Vocational Degree')
  .set(DegreeLevel.Associates, "Associate's Degree")
  .set(DegreeLevel.Bachelors, "Bachelor's Degree")
  .set(DegreeLevel.Masters, "Master's Degree")
  .set(DegreeLevel.PhD, 'Doctorate/PhD')

export enum MilitaryAffiliation {
  Army = 'Army',
  MarineCorps = 'Marine Corps',
  Navy = 'Navy',
  AirForce = 'Air Force',
  CoastGuard = 'Coast Guard',
  SpaceForce = 'Space Force',
}

export enum PayRateTypeEnum {
  Hourly = 'hourly',
  Salary = 'salary',
}

export enum MatchType {
  Exact = 'Exact',
  Partial = 'Partial',
  NotListed = 'NotListed',
}

export enum APPLICATION_STATE {
  APPLIED = 'APPLIED',
  WITHDRAWN = 'WITHDRAWN',
  NOT_INTERESTED = 'NOT_INTERESTED',
  UNRESPONSIVE = 'UNRESPONSIVE',
  HIRED = 'HIRED',
  OFFER_SENT = 'OFFER_SENT',
  INTERVIEWING = 'INTERVIEWING',
  SCREENED = 'SCREENED',
  FINALIST = 'FINALIST',
}
export const APPLICATION_STATE_LIMIT: Record<APPLICATION_STATE, number> = {
  [APPLICATION_STATE.APPLIED]: 50,
  [APPLICATION_STATE.WITHDRAWN]: 10,
  [APPLICATION_STATE.NOT_INTERESTED]: 10,
  [APPLICATION_STATE.UNRESPONSIVE]: 10,
  [APPLICATION_STATE.HIRED]: 10,
  [APPLICATION_STATE.OFFER_SENT]: 25,
  [APPLICATION_STATE.INTERVIEWING]: 50,
  [APPLICATION_STATE.SCREENED]: 25,
  [APPLICATION_STATE.FINALIST]: 25,
} as const

export const ARCHIVED_STATES = [APPLICATION_STATE.WITHDRAWN, APPLICATION_STATE.NOT_INTERESTED]

export const applicationStateMappings = new Map<APPLICATION_STATE, string>()
  .set(APPLICATION_STATE.APPLIED, 'Applied')
  .set(APPLICATION_STATE.SCREENED, 'Screened')
  .set(APPLICATION_STATE.INTERVIEWING, 'Interviewing')
  .set(APPLICATION_STATE.WITHDRAWN, 'Withdrawn')
  .set(APPLICATION_STATE.NOT_INTERESTED, 'Rejected')
  .set(APPLICATION_STATE.UNRESPONSIVE, 'Unresponsive')
  .set(APPLICATION_STATE.HIRED, 'Hired')
  .set(APPLICATION_STATE.OFFER_SENT, 'Offer Sent')
  .set(APPLICATION_STATE.FINALIST, 'Finalist')

export const applicationStateDescriptionMappings = new Map<APPLICATION_STATE, string>()
  .set(APPLICATION_STATE.APPLIED, 'Candidates who have applied to this job post')
  .set(APPLICATION_STATE.INTERVIEWING, 'Candidates who currently have an interview scheduled')
  .set(APPLICATION_STATE.WITHDRAWN, 'Candidates who have withdrawn their application')
  .set(APPLICATION_STATE.NOT_INTERESTED, 'Candidates who have been rejected')
  .set(APPLICATION_STATE.UNRESPONSIVE, 'Candidates who have been unresponsive')
  .set(APPLICATION_STATE.HIRED, 'Candidates who have been hired')
  .set(APPLICATION_STATE.OFFER_SENT, 'Candidates who have been sent an offer')
  .set(APPLICATION_STATE.SCREENED, 'Candidates who have been screened')
  .set(APPLICATION_STATE.FINALIST, 'Candidates who have been interviewed and are being considered for an offer')

export enum ReferenceType {
  Personal = 'Personal',
  Professional = 'Professional',
}
export enum ReferenceRelationship {
  Executive = 'Executive',
  Manager = 'Manager',
  Peer = 'Peer',
  Subordinate = 'Subordinate',
  Customer = 'Customer',
  Vendor = 'Vendor',
  Friend = 'Friend',
}

export enum EmployeeLocation {
  Remote = 'Remote',
  InOffice = 'InOffice',
  Both = 'Both',
  Unspecified = 'Unspecified',
}

export enum CandidateReferrals {
  Cnect = 'cnect',
  Google = 'Google',
  Referral = 'Referral',
  SocialMedia = 'Social Media',
  Other = 'Other',
}

export enum CandidateJobStatus {
  Viewed = 'Viewed',
  Applied = 'Applied',
  JobClosed = 'JobClosed',
  EmployerNotInterested = 'EmployerNotInterested',
  CandidateNotInterested = 'CandidateNotInterested',
}

export const candidateJobStatusRank = {
  [CandidateJobStatus.Viewed]: 0,
  [CandidateJobStatus.Applied]: 1,
  [CandidateJobStatus.JobClosed]: 2,
  [CandidateJobStatus.EmployerNotInterested]: 2,
  [CandidateJobStatus.CandidateNotInterested]: 2,
}

export const CANDIDATE_JOB_INACTIVE = [
  CandidateJobStatus.CandidateNotInterested,
  CandidateJobStatus.EmployerNotInterested,
  CandidateJobStatus.JobClosed,
]

export const candidateJobIsInactive = (status: CandidateJobStatus) => CANDIDATE_JOB_INACTIVE.includes(status)

// ! Be careful when changing as these appear in the DB and would require a migration
export enum RequestedFieldChanged {
  Skills = 'skills',
  Certifications = 'certifications',
  Education = 'education',
  References = 'references',
  WorkHistory = 'workHistory',
  FirstImpression = 'firstImpression',
  PersonalInformation = 'personalInformation',
  Core = 'CORE',
  Portfolio = 'portfolio',
  SocialMedia = 'socialMedia',
  MilitaryAffiliation = 'militaryAffiliation',
  Languages = 'languages',
}

export enum ExperienceLevel {
  Internship = 'Internship',
  EntryLevel = 'EntryLevel',
  Associate = 'Associate',
  MidSeniorLevel = 'MidSeniorLevel',
  SeniorLevel = 'SeniorLevel',
  Manager = 'Manager',
  Director = 'Director',
  Executive = 'Executive',
  NotApplicable = 'NotApplicable',
}

export const experienceLevelMapping = new Map<ExperienceLevel, string>()
  .set(ExperienceLevel.Internship, 'Internship')
  .set(ExperienceLevel.EntryLevel, 'Entry-Level')
  .set(ExperienceLevel.Associate, 'Associate')
  .set(ExperienceLevel.MidSeniorLevel, 'Mid-Senior Level')
  .set(ExperienceLevel.SeniorLevel, 'Senior Level')
  .set(ExperienceLevel.Manager, 'Manager')
  .set(ExperienceLevel.Director, 'Director')
  .set(ExperienceLevel.Executive, 'Executive')
  .set(ExperienceLevel.NotApplicable, 'Not Applicable')

export const dispositionCodeToLabel: Record<DispositionCodes, string> = {
  [DispositionCodes.ApplicantWithdrew]: 'Withdrawn – Applicant Withdrew',
  [DispositionCodes.BasicQualifications]: 'Rejected - Basic Qualifications',
  [DispositionCodes.CandidateUnresponsive]: 'Unresponsive - Candidate Unresponsive',
  [DispositionCodes.CitizenshipRequirements]: 'Rejected - Applicant Does not meet Citizenship Requirement',
  [DispositionCodes.EmployerWithdrewCandidate]: 'Withdrawn - Employer Withdrew on Behalf of Candidate',
  [DispositionCodes.FailedBackgroundCheck]: 'Rejected - Failed Background Check',
  [DispositionCodes.JobFilledOrClosed]: 'Rejected - Job has already been filled from within or has been closed',
  [DispositionCodes.NotBestQualified]: 'Rejected - Not Best Qualified',
  [DispositionCodes.NotSubmittedCitizenshipEVerify]: 'Rejected - Foreign Citizen/e-verify eligibility',
  [DispositionCodes.OfferSent]: 'Offer sent - Offered the Position',
  [DispositionCodes.Other]: 'Rejected - Other',
  [DispositionCodes.RejectedForUnresponsiveness]: 'Rejected - Candidate Unresponsive',
  [DispositionCodes.SelectedForInterview]: 'Interviewing - Selected for Interview',
}
export const dispositionCodeToDescription: Record<DispositionCodes, string> = {
  [DispositionCodes.ApplicantWithdrew]: 'The candidate withdrew their application from consideration.',
  [DispositionCodes.BasicQualifications]: 'The candidate was rejected for not meeting the basic qualifications for the position.',
  [DispositionCodes.CandidateUnresponsive]:
    'The candidate has been marked unresponsive due to unresponsiveness to requests and communication from the employer.',
  [DispositionCodes.CitizenshipRequirements]: 'The candidate was rejected for failing to meet the citizenship requirements.',
  [DispositionCodes.EmployerWithdrewCandidate]: "The employer withdrew this application from consideration at the candidate's request",
  [DispositionCodes.FailedBackgroundCheck]: 'The candidate was rejected because of a failed background check.',
  [DispositionCodes.JobFilledOrClosed]: 'The candidate was rejected because the job has already been filled from within or has been closed.',
  [DispositionCodes.NotBestQualified]: 'The candidate was rejected because there was a better qualified candidate.',
  [DispositionCodes.NotSubmittedCitizenshipEVerify]:
    'The candidate was rejected for failing to submit foreign citizen or e-verify eligibility requirements.',
  [DispositionCodes.OfferSent]: 'The candidate has been offered the position after the interview process.',
  [DispositionCodes.Other]: 'The candidate was rejected for another reason.',
  [DispositionCodes.RejectedForUnresponsiveness]:
    'The candidate was marked as rejected due to unresponsiveness to requests and communication from the employer.',
  [DispositionCodes.SelectedForInterview]: 'The candidate met the basic qualifications for the position and has been selected for an interview.',
}

export enum EmploymentType {
  FullTime = 'FullTime', // These should not be the display values, these represent the actual data
  PartTime = 'PartTime',
  Temporary = 'Temporary',
  Contract = 'Contract',
  Other = 'Other',
}

export const employmentTypeMappings = new Map<EmploymentType, string>()
  .set(EmploymentType.FullTime, 'Full-Time')
  .set(EmploymentType.PartTime, 'Part-Time')
  .set(EmploymentType.Temporary, 'Temporary')
  .set(EmploymentType.Contract, 'Contract')
  .set(EmploymentType.Other, 'Other')

export enum JobProfileStatus {
  Published = 'Published',
  Draft = 'Draft',
  Closed = 'Closed',
  Deleted = 'Deleted',
}

export enum JobStatusEmployer {
  Active = 'Active',
  Draft = 'Draft',
  Closed = 'Closed',
}

export enum JobContentItemType {
  JobPost = 'Job Post',
  JobTemplate = 'Job Template',
}

export enum Browser {
  Opera = 'Opera',
  Chrome = 'Chrome',
  Safari = 'Safari',
  Firefox = 'Firefox',
  IE = 'IE',
}

export enum DialogueStatus {
  SavedAsDraft = 'Draft',
  SavedAsDraftMobile = 'Draft',
  Sent = 'Sent',
  Received = 'Complete',
  New = 'New',
}

export enum JobPostIntegrations {
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  LinkedIn = 'LinkedIn',
  SnapChat = 'SnapChat',
  TikTok = 'TikTok',
  Twitter = 'Twitter',
  CareerBuilder = 'CareerBuilder',
  Dice = 'Dice',
  GlassDoor = 'GlassDoor',
  Indeed = 'Indeed',
  Monster = 'Monster',
  ZipRecruiter = 'ZipRecruiter',
}

export enum EmployerToolbar {
  Request = 'Request',
  Dialogue = 'InterviewSession',
  Messages = 'Messages',
  CandidateStatus = 'CandidateStatus',
}

export enum WebsiteLinks {
  videoTutorials = 'https://cnected.com/first-impression-how-to/',
}
